import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Card, Button, Input, Modal, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { subscriptionPackageAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

const DEFAULT_FORM = {
  packageName: '',
  durationDays: 30,
  price: 0,
  description: '',
  status: 'enabled',
};

const PackageManagementPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [editingPackage, setEditingPackage] = useState(null);

  useEffect(() => {
    loadPackages();
  }, [page, statusFilter]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await subscriptionPackageAPI.getAll(page, 20, search, statusFilter);
      setPackages(response.data.data.packages);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      toast.error('Failed to load subscription packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setPage(1);
    await loadPackages();
  };

  const handleOpenModal = (pkg = null) => {
    setEditingPackage(pkg);
    setFormState(
      pkg
        ? {
            packageName: pkg.packageName,
            durationDays: pkg.durationDays,
            price: pkg.price,
            description: pkg.description,
            status: pkg.status,
          }
        : DEFAULT_FORM
    );
    setModalOpen(true);
  };

  const handleSavePackage = async () => {
    try {
      setLoading(true);
      if (editingPackage) {
        await subscriptionPackageAPI.update(editingPackage._id, formState);
        toast.success('Package updated successfully');
      } else {
        await subscriptionPackageAPI.create(formState);
        toast.success('Package created successfully');
      }
      await loadPackages();
      setModalOpen(false);
      setEditingPackage(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (pkg) => {
    try {
      await subscriptionPackageAPI.toggleStatus(pkg._id, pkg.status === 'enabled' ? 'disabled' : 'enabled');
      toast.success('Package status updated');
      loadPackages();
    } catch (error) {
      toast.error('Failed to update package status');
    }
  };

  const handleDeletePackage = async (pkg) => {
    if (!window.confirm(`Delete package ${pkg.packageName}? This cannot be undone.`)) {
      return;
    }

    try {
      await subscriptionPackageAPI.delete(pkg._id);
      toast.success('Package deleted');
      loadPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Subscription Packages</h1>
          <p className="text-gray-400 mt-2">Create, edit, and manage pricing packages.</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus size={18} />
          <span>Add Package</span>
        </Button>
      </div>

      <Card>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-1 gap-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search packages..."
            />
            <Button type="submit">Search</Button>
          </form>
          <div className="flex gap-3 flex-wrap">
            <Button
              variant={statusFilter === '' ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter('')}
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'enabled' ? 'primary' : 'secondary'}
              onClick={() => setStatusFilter('enabled')}
            >
              Enabled
            </Button>
            <Button
              variant={statusFilter === 'disabled' ? 'danger' : 'secondary'}
              onClick={() => setStatusFilter('disabled')}
            >
              Disabled
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading packages...</div>
        ) : packages.length === 0 ? (
          <div className="py-20 text-center text-gray-400">No packages found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 uppercase text-xs tracking-wide">
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <tr key={pkg._id} className="border-b border-gray-700 hover:bg-gray-800 transition">
                    <td className="px-4 py-4">
                      <p className="text-white font-semibold">{pkg.packageName}</p>
                      <p className="text-gray-400 text-sm mt-1">{pkg.description || 'No description'}</p>
                    </td>
                    <td className="px-4 py-4 text-gray-300">{pkg.durationDays} days</td>
                    <td className="px-4 py-4 text-gray-300">{pkg.price} TK</td>
                    <td className="px-4 py-4">
                      <Badge variant={pkg.status === 'enabled' ? 'success' : 'danger'}>
                        {pkg.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 space-x-2">
                      <Button size="sm" variant="secondary" onClick={() => handleOpenModal(pkg)}>
                        <Pencil size={16} />
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => handleDeletePackage(pkg)}>
                        <Trash2 size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant={pkg.status === 'enabled' ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(pkg)}
                      >
                        {pkg.status === 'enabled' ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingPackage ? 'Edit Package' : 'Create Package'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePackage}>{editingPackage ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <div>
          <Input
            label="Package Name"
            value={formState.packageName}
            onChange={(event) => setFormState({ ...formState, packageName: event.target.value })}
          />
          <Input
            label="Duration (days)"
            type="number"
            value={formState.durationDays}
            onChange={(event) => setFormState({ ...formState, durationDays: Number(event.target.value) })}
          />
          <Input
            label="Price (TK)"
            type="number"
            value={formState.price}
            onChange={(event) => setFormState({ ...formState, price: Number(event.target.value) })}
          />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              rows="4"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
              value={formState.description}
              onChange={(event) => setFormState({ ...formState, description: event.target.value })}
            ></textarea>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={formState.status}
              onChange={(event) => setFormState({ ...formState, status: event.target.value })}
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PackageManagementPage;
